// =============================================================================
// LP Express — "Papabear Label" template renderer.
//
// Renders a `generatedContent.papabearLabel` blob (see
// shared/lp-express-papabear-label.ts) as a warm editorial responsive landing
// page for a physical skincare / wellness product with the Papabear identity.
//
// Section order (canonical): hero → five alternating numbered reasons →
// elevated gallery/buy-box → real-review cards → FAQ accordion → scroll-aware
// sticky bar (appears after ≥15% scroll depth).
//
// • Copy is fully editable via path-based `setText` ops (paths like
//   ["papabearLabel","hero","headline"]).
// • All top-level sections wrapped in EditableElement + one page-level
//   LpReorderGroup; stable keys papabear-label-*.
// • Hero and reason media use durable AI editorial slots with manual overrides
//   first. Real product media stays in the commerce gallery. No art is hotlinked
//   or invented.
// • Purchase panel renders real receipt fields only: current price,
//   strikethrough/savings only if receipt.hasDiscount. No hardcoded prices.
// • CTA in editor is inert/editable; public href uses checkoutUrl.
// • Hero/sticky CTAs scroll to #papabear-label-buy-box rather than checkout;
//   buy-box CTA uses checkout URL.
// • Reviews prefer useLpReviewOverride > Nth reviewDisplay.allReviews. Imported
//   source testimonials remain editable draft inventory in the builder, but
//   are never published as verified social proof without a real review.
// • FAQ uses native <details> accordion.
// =============================================================================
import { useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  Loader2,
  Star,
} from "lucide-react";
import {
  EditableText,
  EditableElement,
  LpReorderGroup,
  LpImageActions,
  LpReviewActions,
  useLpEditor,
  useLpMediaOverride,
  useLpMediaOverridesMap,
  useLpReviewOverride,
  type LpReviewChoice,
} from "@/components/lp-editor";
import type { Product } from "@shared/schema";
import type { LpReceipt, LpReviewDisplay } from "@/lib/lp-express-pricing";
import {
  lpPapabearLabelRegenKey,
  normalizeLpPapabearLabelContent,
  resolvePapabearLabelRootProduct,
  type LpPapabearLabelContent,
  type LpPapabearReviewCard,
} from "@shared/lp-express-papabear-label";

// ── Context type ──────────────────────────────────────────────────────────────

export type PapabearLabelCtx = {
  brand: any;
  offer: { checkoutUrl?: string } | any;
  generatedContent: any;
  logoUrl: string | null;
  bundleProductPhotos: string[];
  bundleImage?: string | null;
  rootProduct?: Product | null;
  products?: Product[];
  receipt?: LpReceipt;
  reviewDisplay?: LpReviewDisplay;
  typography?: { heading?: string; body?: string } | any;
  onRetrySlot?: (
    mergeKey: "papabearLabelImages",
    slotKey: string,
  ) => void;
  onRetryHeroSlot?: (slotIndex: number) => void;
};

// ── Palette ───────────────────────────────────────────────────────────────────
// Resolves to brand CSS vars (brandCssVarsV2 at renderer root).
// Warm cream/ochre editorial defaults matching Papabear identity.
const BRAND_HEAD    = "var(--color-brand-navy, #293d7c)";
const BRAND_ACCENT  = "var(--color-brand-coral, #f26b6e)";
const BRAND_DEEP    = "var(--color-brand-deepnavy, #24366f)";
const BRAND_CREAM   = "var(--color-brand-cream, #f8efdf)";
const BRAND_TEXT    = "var(--color-brand-text, #23335f)";
const BRAND_MUTED   = "var(--color-brand-muted, #657092)";
const BRAND_CARD    = "var(--color-brand-card, #fffdf9)";
const BRAND_BORDER  = "var(--color-brand-lightborder, rgba(41,61,124,0.14))";
const BRAND_GOLD    = "var(--color-brand-gold, #f4bd2e)";
const BRAND_CORAL   = "var(--color-brand-coral, #f26b6e)";

const DISPLAY_FONT = 'var(--font-body, "Helvetica Neue", Arial, sans-serif)';
const EDITORIAL_FONT = 'Georgia, "Times New Roman", serif';

// ── Layout helpers ────────────────────────────────────────────────────────────

const wrap: React.CSSProperties = { maxWidth: 1080, margin: "0 auto", padding: "0 20px" };

const BUY_BOX_ID = "papabear-label-buy-box";

function scrollToBuyBox(e: React.MouseEvent) {
  e.preventDefault();
  const ownerDocument = (e.currentTarget as HTMLElement | null)?.ownerDocument;
  const el = ownerDocument?.getElementById(BUY_BOX_ID);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

// ── Responsive CSS injected once ──────────────────────────────────────────────

const RESPONSIVE_CSS = `
.pb-label-page { color: ${BRAND_TEXT}; overflow: hidden; }
.pb-label-page *, .pb-label-page *::before, .pb-label-page *::after { box-sizing: border-box; }
.pb-label-hero-grid { display:grid; grid-template-columns:1fr; gap:32px; align-items:center; }
.pb-label-hero-copy { max-width: 470px; }
.pb-label-rating-pill { display:inline-flex; align-items:center; gap:7px; background:#fffdf9; border-radius:99px; padding:6px 11px; box-shadow:0 3px 10px rgba(41,61,124,.08); font-size:10px; font-weight:800; color:${BRAND_HEAD}; margin-bottom:16px; }
.pb-label-hero-art { box-shadow: 14px 14px 0 rgba(41,61,124,.07); }
.pb-label-reason-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;
  align-items: center;
  max-width: 1000px;
  margin: 0 auto;
}
.pb-label-reason-copy { max-width:480px; }
.pb-label-reason-observation {
  font-size: 28px;
}
.pb-label-reason-heading {
  font-size: 27px;
}
.pb-label-reason-image { box-shadow: 10px 12px 0 rgba(41,61,124,.06); }
.pb-label-gallery-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  max-width: 940px;
  margin: 0 auto;
  align-items: start;
  overflow: hidden;
  border: 1px solid var(--color-brand-lightborder, rgba(43,31,20,0.12));
  border-radius: 18px;
  background: var(--color-brand-card, #FFF9F2);
  box-shadow: 0 16px 36px rgba(41,61,124,0.10);
}
.pb-label-gallery-media {
  padding: 18px;
  background: #f7eddb;
}
.pb-label-buy-card {
  border-top: 1px solid var(--color-brand-lightborder, rgba(43,31,20,0.12));
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.pb-label-reviews-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}
.pb-label-review-card { box-shadow: 0 9px 20px rgba(41,61,124,.05); }
.pb-label-faq-wrap { max-width: 700px; }
.pb-label-faq-wrap summary::-webkit-details-marker { display:none; }
.pb-label-faq-wrap details[open] .pb-label-faq-plus { transform:rotate(45deg); background:${BRAND_ACCENT}; color:#fff !important; }
@media (min-width: 640px) {
  .pb-label-reason-row {
    grid-template-columns: 1fr 1fr;
  }
  .pb-label-reason-observation {
    font-size: 34px;
  }
  .pb-label-reason-heading {
    font-size: 30px;
  }
  .pb-label-reviews-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (min-width: 900px) {
  .pb-label-hero-grid { grid-template-columns: .96fr 1.04fr; gap:70px; }
  .pb-label-gallery-grid {
    grid-template-columns: 1.02fr 0.98fr;
  }
  .pb-label-buy-card {
    border-top: 0;
    border-left: 1px solid var(--color-brand-lightborder, rgba(43,31,20,0.12));
    align-self: stretch;
  }
  .pb-label-reviews-grid {
    grid-template-columns: 1fr 1fr 1fr;
  }
}
@media (max-width: 639px) {
  .pb-label-hero-art { max-width:430px; margin:0 auto; }
}
.pb-label-buy-intro {
  max-width: 760px;
  margin: 0 auto 26px;
  text-align: center;
}
.pb-label-buy-intro-copy { max-width: 760px; margin: 0 auto; }
.pb-label-buy-intro-heading { font-size: 29px; }
.pb-label-buy-image-frame {
  aspect-ratio: 4 / 3;
  max-height: 430px;
}
@media (min-width: 640px) {
  .pb-label-buy-intro-heading { font-size: 34px; }
}
@media (max-width: 639px) {
  .pb-label-buy-intro { margin-bottom: 20px; }
  .pb-label-buy-image-frame { max-height: none; }
}
@media (max-width: 899px) {
  .pb-label-buy-card { align-self: auto; }
}
`;

// ── Media slot ────────────────────────────────────────────────────────────────

function papabearMediaKey(slot: string): string {
  return `papabear-label:img:${slot}`;
}

function MediaSlot({
  slot,
  label,
  fallbackUrl,
  style,
  fit = "cover",
  loading = false,
  onRegenerate,
  testId,
}: {
  slot: string;
  label?: string;
  fallbackUrl?: string | null;
  style?: React.CSSProperties;
  fit?: "cover" | "contain";
  loading?: boolean;
  onRegenerate?: () => void;
  testId: string;
}) {
  const overrideUrl = useLpMediaOverride(papabearMediaKey(slot));
  const editor = useLpEditor();
  const url = overrideUrl || fallbackUrl || null;
  const actions = (
    <LpImageActions
      slotKey={papabearMediaKey(slot)}
      canRegenerate={!!onRegenerate}
      onRegenerate={onRegenerate}
      testIdPrefix={testId}
    />
  );
  if (!overrideUrl && loading) {
    return (
      <div
        className="group/lpimg relative"
        style={{ width: "100%", height: "100%" }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            width: "100%",
            height: "100%",
            minHeight: 140,
            backgroundColor: BRAND_CREAM,
            ...style,
          }}
          data-testid={`${testId}-loading`}
        >
          <Loader2
            className="h-6 w-6 animate-spin"
            style={{ color: BRAND_ACCENT }}
          />
          {editor ? (
            <span style={{ fontSize: 11, color: BRAND_MUTED }}>
              Creating editorial image…
            </span>
          ) : null}
        </div>
        {actions}
      </div>
    );
  }
  if (url) {
    return (
      <div className="group/lpimg relative" style={{ width: "100%", height: "100%" }}>
        <img
          src={url}
          alt={label || ""}
          loading="lazy"
          style={{
            objectFit: fit,
            objectPosition: "center",
            display: "block",
            width: "100%",
            height: "100%",
            ...style,
          }}
          data-testid={testId}
        />
        {actions}
      </div>
    );
  }
  return (
    <div className="group/lpimg relative" style={{ width: "100%", height: "100%" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          width: "100%",
          height: "100%",
          minHeight: 140,
          backgroundColor: BRAND_CREAM,
          border: `1px dashed ${BRAND_BORDER}`,
          borderRadius: 14,
          ...style,
        }}
        data-testid={testId}
      >
        <ImageIcon className="h-5 w-5" style={{ color: BRAND_MUTED }} />
        {editor && (
          <span style={{ fontSize: 11, color: BRAND_MUTED, textAlign: "center", padding: "0 8px" }}>
            {label || "Upload or pick an image"}
          </span>
        )}
      </div>
      {actions}
    </div>
  );
}

// ── Stars ─────────────────────────────────────────────────────────────────────

function StarRow({ count, size = 14 }: { count: number; size?: number }) {
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          style={{
            width: size,
            height: size,
            color: i < count ? BRAND_GOLD : "#DDD",
            fill: i < count ? BRAND_GOLD : "none",
          }}
        />
      ))}
    </span>
  );
}

function extractVariantLabels(product?: Product): string[] {
  const raw = (product as any)?.variantOptionsJson;
  const values = Array.isArray(raw)
    ? raw.flatMap((item: any) =>
        Array.isArray(item)
          ? item
          : Array.isArray(item?.values)
            ? item.values
            : Array.isArray(item?.options)
              ? item.options
              : [],
      )
    : raw && typeof raw === "object"
      ? Object.values(raw).flatMap((item: any) =>
          Array.isArray(item)
            ? item
            : Array.isArray(item?.values)
              ? item.values
              : Array.isArray(item?.options)
                ? item.options
                : [],
        )
      : [];
  return Array.from(
    new Set(
      values
        .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
        .map((value) => value.trim()),
    ),
  ).slice(0, 12);
}

// ── Section shell ─────────────────────────────────────────────────────────────

function Shell({
  k,
  children,
  style,
}: {
  k: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <EditableElement
      elementKey={`papabear-label-${k}`}
      toolbarSide="left"
      regenBlockKey={lpPapabearLabelRegenKey(k)}
    >
      <section style={style} data-testid={`papabear-label-section-${k}`}>
        {children}
      </section>
    </EditableElement>
  );
}

// ── CTA button ────────────────────────────────────────────────────────────────
// In editor: inert span so copy stays editable. In public: <a> navigates.

function CtaButton({
  label,
  path,
  href,
  onClick,
  testId,
  block = false,
  variant = "primary",
}: {
  label: string;
  path: (string | number)[];
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  testId: string;
  block?: boolean;
  variant?: "primary" | "outline";
}) {
  const editor = useLpEditor();
  const baseStyle: React.CSSProperties = {
    display: block ? "block" : "inline-block",
    width: block ? "100%" : undefined,
    padding: block ? "16px 0" : "13px 32px",
    backgroundColor: variant === "primary" ? BRAND_ACCENT : "transparent",
    color: variant === "primary" ? "#FFFFFF" : BRAND_ACCENT,
    border: variant === "primary" ? "none" : `2px solid ${BRAND_ACCENT}`,
    fontSize: 15,
    fontWeight: 800,
    textAlign: "center",
    borderRadius: 999,
    letterSpacing: "0.03em",
    textDecoration: "none",
    boxShadow: variant === "primary" ? "0 5px 14px rgba(242,107,110,0.24)" : "none",
    cursor: editor ? "default" : "pointer",
  };
  const inner = (
    <EditableText
      value={label}
      path={path}
      as="span"
      noHideDelete
      placeholder="Shop Now"
      testId={`${testId}-label`}
    />
  );
  if (editor) {
    return (
      <span style={{ display: block ? "block" : "inline-block", width: block ? "100%" : undefined }}>
        <span style={baseStyle} data-testid={testId}>
          {inner}
        </span>
      </span>
    );
  }
  if (onClick) {
    return (
      <a href={href || "#"} onClick={onClick} style={baseStyle} data-testid={testId}>
        {inner}
      </a>
    );
  }
  return (
    <a href={href || undefined} style={baseStyle} data-testid={testId}>
      {inner}
    </a>
  );
}

// ── Review card ───────────────────────────────────────────────────────────────
// Precedence: useLpReviewOverride > Nth reviewDisplay.allReviews. Source defaults
// are editor-only drafts. LpReviewActions persists real review selection.

function ReviewCard({
  slot,
  item,
  options,
}: {
  slot: number;
  item?: LpPapabearReviewCard;
  options: LpReviewChoice[];
}) {
  const editor = useLpEditor();
  const slotKey = `papabear-label:reviews:${slot}`;
  const override = useLpReviewOverride(slotKey);
  const realOption: LpReviewChoice | null = override ?? options[slot] ?? null;
  if (!realOption && !editor) return null;

  // When a real review is selected it wins over the source item entirely.
  const displayName = realOption
    ? realOption.reviewer || "Verified customer"
    : item?.attribution ?? "";
  const displayQuote = realOption ? realOption.reviewText : item?.quote ?? "";
  const displayRating = realOption ? realOption.rating : 5;
  // Title is only available from source items (real reviews have none).
  const displayTitle = realOption ? null : item?.title ?? "";

  const basePath: (string | number)[] = ["papabearLabel", "reviews", "items", slot];

  return (
    <div
      className="group/lpreview relative pb-label-review-card"
      style={{
        backgroundColor: "#fffefd",
        borderRadius: 12,
        border: `1px solid ${BRAND_BORDER}`,
        padding: "20px 20px 18px",
      }}
      data-testid={`papabear-label-review-${slot}`}
    >
      {options.length > 0 && (
        <LpReviewActions
          slotKey={slotKey}
          reviews={options}
          testIdPrefix={`papabear-label-review-${slot}`}
        />
      )}
      {realOption && (
        <div style={{ marginBottom: 10 }}>
          <StarRow count={displayRating} size={14} />
        </div>
      )}
      {/* Title — source only, never for real reviews */}
      {displayTitle ? (
        <EditableText
          value={displayTitle}
          path={[...basePath, "title"]}
          as="p"
          style={{ fontSize: 14, fontWeight: 700, color: BRAND_HEAD, margin: "0 0 8px" }}
          placeholder="Review title"
          testId={`text-papabear-label-review-title-${slot}`}
        />
      ) : null}
      {/* Quote */}
      {realOption ? (
          <p style={{ fontFamily: EDITORIAL_FONT, fontSize: 14.5, lineHeight: 1.7, color: BRAND_TEXT, margin: "0 0 14px" }}>
          &ldquo;{displayQuote}&rdquo;
        </p>
      ) : (
        <EditableText
          value={displayQuote}
          path={[...basePath, "quote"]}
          as="p"
          multiline
            style={{ fontFamily: EDITORIAL_FONT, fontSize: 14.5, lineHeight: 1.7, color: BRAND_TEXT, margin: "0 0 14px" }}
          placeholder="Review quote"
          testId={`text-papabear-label-review-quote-${slot}`}
        />
      )}
       {/* Attribution */}
       <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
         <div style={{ minWidth: 0 }}>
          {realOption ? (
            <p style={{ fontSize: 13, fontWeight: 700, color: BRAND_HEAD, margin: 0 }}>
              {displayName}
            </p>
          ) : (
            <EditableText
              value={item?.attribution ?? ""}
              path={[...basePath, "attribution"]}
              as="p"
              style={{ fontSize: 13, fontWeight: 700, color: BRAND_HEAD, margin: 0 }}
              placeholder="Customer name"
              testId={`text-papabear-label-review-attr-${slot}`}
            />
          )}
            <p style={{ fontSize: 10.5, color: BRAND_ACCENT, margin: "3px 0 0", fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase" }}>
            {realOption ? "Verified buyer" : "Imported draft — select a real review"}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── FAQ item (native <details>) ───────────────────────────────────────────────

function FaqItem({
  q,
  a,
  qPath,
  aPath,
  testIdBase,
}: {
  q: string;
  a: string;
  qPath: (string | number)[];
  aPath: (string | number)[];
  testIdBase: string;
}) {
  return (
    <details
      style={{ borderBottom: `1px solid ${BRAND_BORDER}` }}
      data-testid={`${testIdBase}-item`}
    >
      <summary
        style={{
          cursor: "pointer",
          listStyle: "none",
          padding: "18px 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <EditableText
          value={q}
          path={qPath}
          as="span"
          style={{ fontSize: 15.5, fontWeight: 700, color: BRAND_HEAD, flex: 1 }}
          placeholder="Question"
          testId={`${testIdBase}-q`}
        />
        <span
          className="pb-label-faq-plus"
          style={{ fontSize: 18, fontWeight: 400, color: BRAND_ACCENT, flexShrink: 0, lineHeight: "22px", textAlign: "center", width: 22, height: 22, borderRadius: "50%", backgroundColor: "rgba(242,107,110,.11)", transition: "transform .18s ease, background-color .18s ease, color .18s ease" }}
        >
          +
        </span>
      </summary>
      <div style={{ paddingBottom: 18 }}>
        <EditableText
          value={a}
          path={aPath}
          as="p"
          multiline
          style={{ fontFamily: EDITORIAL_FONT, fontSize: 14.5, lineHeight: 1.7, color: BRAND_MUTED, margin: 0 }}
          placeholder="Answer"
          testId={`${testIdBase}-a`}
        />
      </div>
    </details>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function LpExpressPapabearLabel({ ctx }: { ctx: PapabearLabelCtx }) {
  const pb: LpPapabearLabelContent = normalizeLpPapabearLabelContent(
    ctx.generatedContent?.papabearLabel,
  );
  const editor = useLpEditor();
  const checkoutUrl: string = ctx.offer?.checkoutUrl || "";
  const receipt = ctx.receipt;
  const reviewDisplay = ctx.reviewDisplay;
  const mediaOverrides = useLpMediaOverridesMap();
  const [galleryIndex, setGalleryIndex] = useState(0);
  const gallerySlots = [
    "gallery-01",
    "gallery-02",
    "gallery-03",
    "gallery-04",
    "gallery-05",
    "gallery-06",
  ] as const;
  const galleryFallbacks = [
    ctx.bundleImage || ctx.bundleProductPhotos[0] || null,
    ctx.bundleProductPhotos[1] || null,
    ctx.bundleProductPhotos[2] || null,
    ctx.bundleProductPhotos[3] || null,
    ctx.bundleProductPhotos[4] || null,
    ctx.bundleProductPhotos[5] || null,
  ];
  const populatedGalleryItems = gallerySlots
    .map((slot, index) => ({
      slot,
      fallbackUrl: galleryFallbacks[index],
      overrideUrl: mediaOverrides[papabearMediaKey(slot)]?.url,
    }))
    // Do not manufacture a six-tile wall by repeating the first product photo.
    // Existing uploaded overrides remain visible, while an entirely media-less
    // product still gets one empty editor slot for a real upload.
    .filter((item) => item.overrideUrl || item.fallbackUrl);
  const galleryItems = populatedGalleryItems.length > 0
    ? populatedGalleryItems
    : [{ slot: gallerySlots[0], fallbackUrl: null, overrideUrl: undefined }];
  const safeGalleryIndex = Math.min(galleryIndex, galleryItems.length - 1);
  const activeGalleryItem = galleryItems[safeGalleryIndex];
  const galleryThumbStart = Math.floor(safeGalleryIndex / 3) * 3;
  const visibleGalleryItems = galleryItems.slice(
    galleryThumbStart,
    galleryThumbStart + 3,
  );
  const heroImageUrl =
    Array.isArray(ctx.generatedContent?.heroImages) &&
    typeof ctx.generatedContent.heroImages[0] === "string"
      ? ctx.generatedContent.heroImages[0]
      : null;
  const heroImageLoading =
    !heroImageUrl && Boolean(ctx.generatedContent?.heroImagesLoading);
  const reasonImages = Array.isArray(
    ctx.generatedContent?.papabearLabelImages,
  )
    ? ctx.generatedContent.papabearLabelImages
    : [];

  // Build the real-review options list for the review picker.
  const realReviews: LpReviewChoice[] = (reviewDisplay?.allReviews ?? []).map((r) => ({
    reviewer: r.reviewer,
    reviewText: r.reviewText,
    rating: r.rating,
  }));
  const primaryProduct = resolvePapabearLabelRootProduct(
    ctx.rootProduct,
    ctx.products ?? [],
  );
  const liveProductName =
    typeof (primaryProduct as any)?.name === "string" &&
    (primaryProduct as any).name.trim()
      ? (primaryProduct as any).name.trim()
      : null;
  const isLiveBundle =
    Boolean((primaryProduct as any)?.isBundle);
  const liveVariantLabels = extractVariantLabels(primaryProduct);

  // Path helper
  const P = (...p: (string | number)[]): (string | number)[] => ["papabearLabel", ...p];

  // ── Sticky bar: appears after ≥15% scroll depth ───────────────────────────
  const [stickyVisible, setStickyVisible] = useState(false);
  const stickyShownRef = useRef(false);
  const pageRootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const ownerDocument = pageRootRef.current?.ownerDocument;
    const ownerWindow = ownerDocument?.defaultView;
    if (!ownerDocument || !ownerWindow) return;
    const doc = ownerDocument;
    const win = ownerWindow;
    function onScroll() {
      const scroller = doc.scrollingElement ?? doc.documentElement;
      const scrollable = scroller.scrollHeight - win.innerHeight;
      const depth = scrollable > 0 ? scroller.scrollTop / scrollable : 0;
      if (depth >= 0.15 && !stickyShownRef.current) {
        stickyShownRef.current = true;
        setStickyVisible(true);
      }
    }
    win.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => win.removeEventListener("scroll", onScroll);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // HERO
  // ─────────────────────────────────────────────────────────────────────────
  const heroSection = (
    <Shell
      k="hero"
      style={{ backgroundColor: BRAND_CREAM, padding: "30px 0 38px", borderBottom: `1px solid ${BRAND_BORDER}` }}
    >
      <div
        style={{ ...wrap, maxWidth: 900 }}
        className="pb-label-hero-grid"
      >
        {/* Copy */}
        <div className="pb-label-hero-copy">
          {reviewDisplay?.hasReviews && (
            <div className="pb-label-rating-pill" data-testid="papabear-label-hero-rating">
              <StarRow count={Math.round(Number(reviewDisplay.ratingText) || 5)} size={10} />
              <span>{reviewDisplay.ratingText} stars</span>
              <span style={{ opacity: .42 }}>·</span>
              <span>{reviewDisplay.reviewCountText}</span>
            </div>
          )}
          <EditableText
            value={pb.hero.eyebrow}
            path={P("hero", "eyebrow")}
            as="div"
            style={{
              fontSize: 10, fontWeight: 900, letterSpacing: "0.14em",
              textTransform: "uppercase", color: BRAND_CORAL, marginBottom: 8,
            }}
            placeholder="FOR DRY SKIN"
            testId="text-papabear-label-hero-eyebrow"
          />
          <EditableText
            value={pb.hero.headline}
            path={P("hero", "headline")}
            as="h1"
            style={{
              fontFamily: DISPLAY_FONT, fontSize: 38, lineHeight: 1.06,
              fontWeight: 900, color: BRAND_HEAD, margin: "0 0 13px", letterSpacing: "-.035em",
            }}
            placeholder="Headline"
            testId="text-papabear-label-hero-headline"
          />
          <EditableText
            value={pb.hero.body}
            path={P("hero", "body")}
            as="p"
            multiline
            style={{ fontFamily: EDITORIAL_FONT, fontSize: 14, lineHeight: 1.6, color: BRAND_TEXT, margin: "0 0 20px", maxWidth: 410 }}
            placeholder="Hero body copy"
            testId="text-papabear-label-hero-body"
          />
          <CtaButton
            label={pb.hero.cta}
            path={P("hero", "cta")}
            href={`#${BUY_BOX_ID}`}
            onClick={!editor ? scrollToBuyBox : undefined}
            testId="button-papabear-label-hero-cta"
          />
          {/* Trust items */}
          {pb.hero.trustItems.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 16 }}>
              {pb.hero.trustItems.map((t, i) => (
                <span
                  key={i}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: BRAND_MUTED }}
                >
                  {i > 0 ? (
                    <span aria-hidden="true" style={{ color: BRAND_CORAL, fontSize: 11 }}>•</span>
                  ) : null}
                  <EditableText
                    value={t.label}
                    path={P("hero", "trustItems", i, "label")}
                    as="span"
                    placeholder="Trust item"
                    testId={`text-papabear-label-hero-trust-${i}`}
                  />
                </span>
              ))}
            </div>
          )}
        </div>
        {/* Image */}
        <div
          className="pb-label-hero-art"
          style={{
            borderRadius: 12,
            overflow: "hidden",
            aspectRatio: "1/1",
            minHeight: 300,
            backgroundColor: BRAND_CARD,
            padding: 0,
          }}
        >
          <MediaSlot
            slot="hero-image"
            label="Editorial hero image"
            // The hero is intentionally editorial-only. Product media belongs
            // in the commerce panel below; never promote it into the hero.
            fallbackUrl={heroImageUrl}
            loading={heroImageLoading}
            onRegenerate={
              ctx.onRetryHeroSlot
                ? () => ctx.onRetryHeroSlot?.(0)
                : undefined
            }
            fit="cover"
            style={{ backgroundColor: "#F5EADB" }}
            testId="img-papabear-label-hero"
          />
        </div>
      </div>
    </Shell>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // FIVE ALTERNATING NUMBERED REASONS
  // ─────────────────────────────────────────────────────────────────────────
  const reasonsSection = (
    <Shell k="reasons" style={{ backgroundColor: "#fffefd", padding: "62px 0" }}>
      <div style={wrap}>
        <div style={{ display: "flex", flexDirection: "column", gap: 68 }}>
          {pb.reasons.items.map((reason, i) => {
            const isEven = i % 2 === 0;
            // Slot key aligned with LP_PAPABEAR_MEDIA_SLOTS (reason-01-image .. reason-05-image)
            const imgSlot = `reason-0${i + 1}-image`;
            const generatedImage = reasonImages.find(
              (slot: any) => slot?.key === imgSlot,
            );
            const generatedUrl =
              generatedImage?.imageStatus !== "failed"
                ? generatedImage?.imageUrl
                : null;
            return (
              <div
                key={i}
                className="pb-label-reason-row"
                data-testid={`papabear-label-reason-${i}`}
              >
                {/* Image — alternates sides on desktop */}
                <div
                  className="pb-label-reason-image"
                  style={{
                    order: isEven ? 0 : 1,
                    borderRadius: 12,
                    overflow: "hidden",
                    aspectRatio: "4/3",
                    minHeight: 200,
                  }}
                >
                  <MediaSlot
                    slot={imgSlot}
                     label={
                       generatedImage?.altText ||
                       `Reason ${i + 1} editorial photo`
                     }
                     fallbackUrl={generatedUrl || null}
                     loading={
                       !generatedUrl &&
                       (generatedImage?.imageLoading ||
                         generatedImage?.imageStatus === "pending")
                     }
                     onRegenerate={
                       ctx.onRetrySlot
                         ? () =>
                             ctx.onRetrySlot?.(
                               "papabearLabelImages",
                               imgSlot,
                             )
                         : undefined
                     }
                    fit="cover"
                    style={{ backgroundColor: "#FFFDF8" }}
                    testId={`img-papabear-label-reason-${i}`}
                  />
                </div>
                {/* Copy */}
                <div className="pb-label-reason-copy" style={{ order: isEven ? 1 : 0, padding: "8px 0" }}>
                  {/* Large ghosted number */}
                  <span
                    style={{
                      fontFamily: DISPLAY_FONT,
                      fontSize: 12,
                      fontWeight: 900,
                      color: BRAND_CORAL,
                      display: "block",
                      lineHeight: 1,
                      marginBottom: 8,
                      letterSpacing: ".12em",
                      userSelect: "none",
                    }}
                  >
                    <EditableText
                      value={reason.number}
                      path={P("reasons", "items", i, "number")}
                      as="span"
                      noHideDelete
                      placeholder={`0${i + 1}`}
                      testId={`text-papabear-label-reason-number-${i}`}
                    />
                  </span>
                  <EditableText
                    value={reason.label}
                    path={P("reasons", "items", i, "label")}
                    as="div"
                    style={{
                      display: "inline-block",
                      width: "fit-content",
                      padding: "5px 9px",
                      backgroundColor: "rgba(242, 107, 110, 0.11)",
                      color: BRAND_CORAL,
                      fontSize: 10,
                      lineHeight: 1,
                      fontWeight: 900,
                      letterSpacing: ".12em",
                      textTransform: "uppercase",
                      marginBottom: 14,
                    }}
                    placeholder="FOUND ON LABEL"
                    testId={`text-papabear-label-reason-label-${i}`}
                  />
                  <EditableText
                    value={reason.observation}
                    path={P("reasons", "items", i, "observation")}
                    as="h3"
                    multiline
                    className="pb-label-reason-observation"
                    style={{
                      fontFamily: EDITORIAL_FONT,
                      fontStyle: "italic",
                      fontWeight: 700,
                      color: BRAND_CORAL,
                      margin: "0 0 12px",
                      lineHeight: 1.16,
                      letterSpacing: "-.025em",
                    }}
                    placeholder="Editorial observation"
                    testId={`text-papabear-label-reason-observation-${i}`}
                  />
                  <span
                    aria-hidden="true"
                    style={{
                      display: "block",
                      color: BRAND_CORAL,
                      fontFamily: EDITORIAL_FONT,
                      fontSize: 28,
                      lineHeight: 1,
                      marginBottom: 14,
                    }}
                    data-testid={`papabear-label-reason-arrow-${i}`}
                  >
                    →
                  </span>
                  <EditableText
                    value={reason.heading}
                    path={P("reasons", "items", i, "heading")}
                    as="h3"
                    className="pb-label-reason-heading"
                    style={{
                      fontFamily: DISPLAY_FONT, fontWeight: 900,
                      color: BRAND_HEAD, margin: "0 0 13px", lineHeight: 1.12, letterSpacing: "-.025em",
                    }}
                    placeholder="Reason heading"
                    testId={`text-papabear-label-reason-heading-${i}`}
                  />
                  <EditableText
                    value={reason.body}
                    path={P("reasons", "items", i, "body")}
                    as="p"
                    multiline
                    style={{ fontFamily: EDITORIAL_FONT, fontSize: 14, lineHeight: 1.68, color: BRAND_TEXT, margin: 0 }}
                    placeholder="Reason body copy"
                    testId={`text-papabear-label-reason-body-${i}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Shell>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // ELEVATED GALLERY / BUY BOX
  // ─────────────────────────────────────────────────────────────────────────
  const buyBoxSection = (
    <Shell k="buyBox" style={{ backgroundColor: BRAND_CREAM, padding: "54px 0 62px" }}>
      <div id={BUY_BOX_ID} style={{ ...wrap, scrollMarginTop: 60 }}>
        <div className="pb-label-buy-intro">
          <div className="pb-label-buy-intro-copy">
            <EditableText
              value={pb.buyBox.eyebrow}
              path={P("buyBox", "eyebrow")}
              as="div"
              style={{
                color: BRAND_CORAL,
                fontSize: 10,
                fontWeight: 900,
                letterSpacing: ".15em",
                margin: "0 0 7px",
                textTransform: "uppercase",
              }}
              placeholder="GET STARTED"
              testId="text-papabear-label-buybox-section-eyebrow"
            />
            {liveProductName ? (
              <h2
                className="pb-label-buy-intro-heading"
                style={{
                  fontFamily: DISPLAY_FONT,
                  fontWeight: 900,
                  color: BRAND_HEAD,
                  lineHeight: 1.08,
                  letterSpacing: "-.035em",
                  margin: "0 0 10px",
                }}
                data-testid="text-papabear-label-buybox-section-heading"
              >
                {liveProductName}
              </h2>
            ) : (
              <EditableText
                value={pb.buyBox.productHeading}
                path={P("buyBox", "productHeading")}
                as="h2"
                className="pb-label-buy-intro-heading"
                style={{
                  fontFamily: DISPLAY_FONT,
                  fontWeight: 900,
                  color: BRAND_HEAD,
                  lineHeight: 1.08,
                  letterSpacing: "-.035em",
                  margin: "0 0 10px",
                }}
                placeholder="Product name"
                testId="text-papabear-label-buybox-section-heading"
              />
            )}
            <EditableText
              value={pb.buyBox.subhead}
              path={P("buyBox", "subhead")}
              as="p"
              multiline
              style={{
                fontFamily: EDITORIAL_FONT,
                color: BRAND_TEXT,
                fontSize: 15,
                lineHeight: 1.5,
                margin: "0 auto",
                maxWidth: 680,
              }}
              placeholder="Subheadline"
              testId="text-papabear-label-buybox-section-subhead"
            />
          </div>
        </div>
        <div className="pb-label-gallery-grid">
          {/* Gallery column */}
          <div className="pb-label-gallery-media">
            <div
              style={{
                borderRadius: 10,
                overflow: "hidden",
                aspectRatio: "4/3",
                maxHeight: 430,
                marginBottom: 12,
                backgroundColor: BRAND_CARD,
                border: `1px solid ${BRAND_BORDER}`,
              }}
              className="pb-label-buy-image-frame"
            >
              <MediaSlot
                slot={activeGalleryItem.slot}
                label={`Gallery photo ${safeGalleryIndex + 1}`}
                fallbackUrl={activeGalleryItem.fallbackUrl}
                fit="contain"
                style={{ backgroundColor: "#FFFDF8", padding: 12 }}
                testId="img-papabear-label-gallery-01"
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                justifyContent: "center",
                minHeight: 70,
              }}
            >
              {galleryThumbStart > 0 && (
                <button
                  type="button"
                  aria-label={pb.buyBox.galleryPrevLabel}
                  onClick={() => setGalleryIndex(Math.max(0, galleryThumbStart - 1))}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 999,
                    border: `1px solid ${BRAND_BORDER}`,
                    background: "#FFFFFF",
                    color: BRAND_HEAD,
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                    flex: "0 0 30px",
                  }}
                  data-testid="button-papabear-label-gallery-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )}
              {visibleGalleryItems.map((item, thumbIndex) => {
                const itemIndex = galleryThumbStart + thumbIndex;
                return (
                  <div
                    key={item.slot}
                    role="button"
                    tabIndex={0}
                    onClick={() => setGalleryIndex(itemIndex)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setGalleryIndex(itemIndex);
                      }
                    }}
                    aria-pressed={safeGalleryIndex === itemIndex}
                    style={{
                      borderRadius: 10,
                      overflow: "hidden",
                      width: 68,
                      height: 68,
                      flex: "0 0 68px",
                      padding: 0,
                      border: safeGalleryIndex === itemIndex
                        ? `2px solid ${BRAND_ACCENT}`
                        : `1px solid ${BRAND_BORDER}`,
                      background: BRAND_CARD,
                      cursor: "pointer",
                    }}
                  >
                    <MediaSlot
                      slot={item.slot}
                      label={`Gallery photo ${itemIndex + 1}`}
                      fallbackUrl={item.fallbackUrl}
                      fit="contain"
                      style={{ backgroundColor: "#FFFDF8", padding: 2 }}
                      testId={`img-papabear-label-${item.slot}`}
                    />
                  </div>
                );
              })}
              {galleryThumbStart + 3 < galleryItems.length && (
                <button
                  type="button"
                  aria-label={pb.buyBox.galleryNextLabel}
                  onClick={() =>
                    setGalleryIndex(
                      Math.min(galleryItems.length - 1, galleryThumbStart + 3),
                    )
                  }
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 999,
                    border: `1px solid ${BRAND_BORDER}`,
                    background: "#FFFFFF",
                    color: BRAND_HEAD,
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                    flex: "0 0 30px",
                  }}
                  data-testid="button-papabear-label-gallery-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Buy box card */}
          <div
            className="pb-label-buy-card"
            style={{
               backgroundColor: "#fffefd",
               padding: "25px 27px 22px",
            }}
          >
            {liveProductName ? (
              <h2
                style={{
                   fontFamily: DISPLAY_FONT, fontSize: 25, fontWeight: 900,
                  color: BRAND_HEAD, margin: "0 0 4px", lineHeight: 1.15,
                }}
                data-testid="text-papabear-label-buybox-heading"
              >
                {liveProductName}
              </h2>
            ) : (
              <EditableText
                value={pb.buyBox.productHeading}
                path={P("buyBox", "productHeading")}
                as="h2"
                style={{
                   fontFamily: DISPLAY_FONT, fontSize: 25, fontWeight: 900,
                  color: BRAND_HEAD, margin: "0 0 4px", lineHeight: 1.15,
                }}
                placeholder="Product name"
                testId="text-papabear-label-buybox-heading"
              />
            )}
            {/* Price — derived from receipt only; never hardcoded */}
            {receipt && (
               <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "14px 0 16px", paddingBottom: 14, borderBottom: `1px dashed ${BRAND_BORDER}` }}>
                <span
                   style={{ fontSize: 30, fontWeight: 900, color: BRAND_HEAD }}
                  data-testid="text-papabear-label-price"
                >
                  {receipt.priceTodayLabel}
                </span>
                {receipt.hasDiscount && receipt.retailLabel && (
                  <span
                    style={{ fontSize: 18, color: "#AAA", textDecoration: "line-through" }}
                    data-testid="text-papabear-label-retail"
                  >
                    {receipt.retailLabel}
                  </span>
                )}
                {receipt.hasDiscount && receipt.discountPercent != null && (
                  <span
                    style={{
                      backgroundColor: BRAND_CORAL, color: "#fff", fontWeight: 800,
                      fontSize: 11, padding: "3px 9px", borderRadius: 6, letterSpacing: "0.06em",
                    }}
                    data-testid="badge-papabear-label-save"
                  >
                    SAVE {receipt.discountPercent}%
                  </span>
                )}
              </div>
            )}

            {/* Included features bullets */}
             {isLiveBundle && pb.buyBox.includedFeatures.length > 0 && (
               <div style={{ margin: "12px 0 16px" }}>
                {pb.buyBox.includedFeatures.map((f, i) => (
                     <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 7 }}>
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: BRAND_ACCENT }} />
                    <EditableText
                      value={f}
                      path={P("buyBox", "includedFeatures", i)}
                      as="span"
                       style={{ fontSize: 13.5, lineHeight: 1.45, color: BRAND_TEXT }}
                      placeholder="Included feature"
                      testId={`text-papabear-label-buybox-feature-${i}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Scent selector label */}
             {liveVariantLabels.length > 0 && (
               <div style={{ margin: "14px 0" }}>
                <EditableText
                  value={pb.buyBox.scentFieldLabel}
                  path={P("buyBox", "scentFieldLabel")}
                  as="label"
                  style={{ fontSize: 12, fontWeight: 700, color: BRAND_MUTED, display: "block", marginBottom: 8 }}
                  placeholder="Choose your scent"
                  testId="text-papabear-label-buybox-scent-label"
                />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {liveVariantLabels.map((label) => (
                    <span
                      key={label}
                      style={{
                        fontSize: 12.5, fontWeight: 600, color: BRAND_DEEP,
                        border: `1px solid ${BRAND_BORDER}`, borderRadius: 8,
                         padding: "5px 10px", backgroundColor: "#FAFAFA",
                      }}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Purchase-mode labels from the source export remain normalized in
                saved content as import inventory, but they are not checkout
                controls. Showing them in the editor made inert template copy
                look functional. Render only live product variant labels above;
                unsupported subscription/one-time modes stay hidden everywhere. */}

            {/* Add to cart CTA — uses checkoutUrl directly */}
             <div style={{ marginTop: 16 }}>
              <CtaButton
                label={pb.buyBox.addToCartLabel}
                path={P("buyBox", "addToCartLabel")}
                href={checkoutUrl}
                testId="button-papabear-label-buybox-cta"
                block
              />
            </div>

            {/* Shipping note */}
            <EditableText
              value={pb.buyBox.shippingNote}
              path={P("buyBox", "shippingNote")}
              as="p"
              style={{ fontSize: 12, color: BRAND_MUTED, textAlign: "center", marginTop: 10 }}
              placeholder="Free shipping over $75"
              testId="text-papabear-label-buybox-shipping"
            />
          </div>
        </div>
      </div>
    </Shell>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // REVIEWS
  // ─────────────────────────────────────────────────────────────────────────
  const reviewsSection = (
    <Shell k="reviews" style={{ backgroundColor: BRAND_CREAM, padding: "60px 0 68px" }}>
      <div style={{ ...wrap, maxWidth: 920 }}>
        <div style={{ maxWidth: 600, margin: "0 0 28px" }}>
          <EditableText
            value={pb.reviews.eyebrow}
            path={P("reviews", "eyebrow")}
            as="div"
            style={{
              fontSize: 10, fontWeight: 900, letterSpacing: "0.15em",
              textTransform: "uppercase", color: BRAND_CORAL, marginBottom: 8,
            }}
            placeholder="REAL CUSTOMERS"
            testId="text-papabear-label-reviews-eyebrow"
          />
          <EditableText
            value={pb.reviews.heading}
            path={P("reviews", "heading")}
            as="h2"
            style={{
              fontFamily: DISPLAY_FONT, fontSize: 32, lineHeight: 1.1,
              fontWeight: 900, color: BRAND_HEAD, margin: 0,
            }}
            placeholder="Reviews heading"
            testId="text-papabear-label-reviews-heading"
          />
          {/* Aggregate rating — ONLY when real reviews exist */}
          {reviewDisplay?.hasReviews && (
            <div
               style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}
              data-testid="papabear-label-rating-bar"
            >
              <StarRow count={Math.round(Number(reviewDisplay.ratingText) || 5)} size={16} />
              <span style={{ fontSize: 14, fontWeight: 600, color: BRAND_TEXT }}>
                {reviewDisplay.ratingText} · {reviewDisplay.reviewCountText} reviews
              </span>
            </div>
          )}
          {/* Rating summary label shown only when no real reviews and there's fallback copy */}
          {editor && !reviewDisplay?.hasReviews && pb.reviews.ratingSummaryLabel && (
            <EditableText
              value={pb.reviews.ratingSummaryLabel}
              path={P("reviews", "ratingSummaryLabel")}
              as="p"
              style={{ fontSize: 13, color: BRAND_MUTED, marginTop: 10 }}
              placeholder="Verified customer reviews"
              testId="text-papabear-label-reviews-rating-label"
            />
          )}
        </div>
        <div className="pb-label-reviews-grid">
          {pb.reviews.items.map((item, i) => (
            <ReviewCard key={i} slot={i} item={item} options={realReviews} />
          ))}
        </div>
      </div>
    </Shell>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // FAQ
  // ─────────────────────────────────────────────────────────────────────────
  const faqSection = (
    <Shell k="faq" style={{ backgroundColor: "#fffefd", padding: "66px 0 78px" }}>
      <div className="pb-label-faq-wrap" style={{ ...wrap, maxWidth: 740 }}>
        <div style={{ marginBottom: 30 }}>
          <EditableText
            value={pb.faq.eyebrow}
            path={P("faq", "eyebrow")}
            as="div"
            style={{
              fontSize: 10, fontWeight: 900, letterSpacing: "0.15em",
              textTransform: "uppercase", color: BRAND_CORAL, marginBottom: 8,
            }}
            placeholder="FREQUENTLY ASKED QUESTIONS"
            testId="text-papabear-label-faq-eyebrow"
          />
          <EditableText
            value={pb.faq.heading}
            path={P("faq", "heading")}
            as="h2"
            style={{
              fontFamily: DISPLAY_FONT, fontSize: 32, lineHeight: 1.1,
              fontWeight: 900, color: BRAND_HEAD, margin: 0,
            }}
            placeholder="Got a question?"
            testId="text-papabear-label-faq-heading"
          />
        </div>
        <div style={{ borderTop: `1px solid ${BRAND_BORDER}` }}>
          {pb.faq.items.map((item, i) => (
            <FaqItem
              key={i}
              q={item.q}
              a={item.a}
              qPath={P("faq", "items", i, "q")}
              aPath={P("faq", "items", i, "a")}
              testIdBase={`papabear-label-faq-${i}`}
            />
          ))}
        </div>
      </div>
    </Shell>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // STICKY BAR (scroll-aware, fixed bottom, appears after ≥15% depth)
  // ─────────────────────────────────────────────────────────────────────────
  const stickyBarSection = (
    <EditableElement
      elementKey="papabear-label-stickyBar"
      toolbarSide="left"
      regenBlockKey={lpPapabearLabelRegenKey("stickyBar")}
    >
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backgroundColor: BRAND_DEEP,
          padding: "12px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14,
          boxShadow: "0 -4px 20px rgba(0,0,0,0.22)",
          transform: stickyVisible ? "translateY(0)" : "translateY(110%)",
          transition: "transform 0.35s ease",
        }}
        data-testid="papabear-label-sticky-bar"
      >
        <EditableText
          value={pb.stickyBar.offer}
          path={P("stickyBar", "offer")}
          as="span"
          style={{ fontSize: 13.5, fontWeight: 700, color: "#FFFFFF", flex: 1, minWidth: 0 }}
           placeholder="Free shipping on orders over $75"
          testId="text-papabear-label-sticky-offer"
        />
        <CtaButton
          label={pb.stickyBar.cta}
          path={P("stickyBar", "cta")}
          href={`#${BUY_BOX_ID}`}
          onClick={!editor ? scrollToBuyBox : undefined}
          testId="button-papabear-label-sticky-cta"
        />
      </div>
    </EditableElement>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // PAGE ASSEMBLY — one LpReorderGroup for all top-level sections
  // ─────────────────────────────────────────────────────────────────────────

  const sectionItems: { key: string; node: React.ReactNode }[] = [
    { key: "papabear-label-hero",    node: heroSection },
    { key: "papabear-label-reasons", node: reasonsSection },
    { key: "papabear-label-buyBox",  node: buyBoxSection },
    { key: "papabear-label-reviews", node: reviewsSection },
    { key: "papabear-label-faq",     node: faqSection },
  ];

  return (
    <div
      ref={pageRootRef}
      className="pb-label-page"
      style={{ fontFamily: "var(--font-body, system-ui, sans-serif)" }}
      data-testid="papabear-label-page"
    >
      <style dangerouslySetInnerHTML={{ __html: RESPONSIVE_CSS }} />
      <LpReorderGroup groupKey="papabear-label-page" items={sectionItems} />
      {stickyBarSection}
    </div>
  );
}
